import React from 'react';
import ReactDom from 'react-dom';
import {Provider as ReduxProvider} from 'react-redux';
import {Router} from 'react-router-dom';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {queryClient} from 'utils/queryClient';
import {addLocaleData} from '@meiko/react-intl';
import IntlProvider from 'views/IntlProvider';
import {ToastProvider} from 'contexts/ToastContext';
//import registerServiceWorker from 'io/registerServiceWorker';
import createStoreActionHook from 'services/createStoreActionHook';
import createStore from 'services/createStore';
import createApi from 'services/createApi';
import services from 'services';
import enLocale from '@meiko/react-intl/locale-data/en';
import fiLocale from '@meiko/react-intl/locale-data/fi';
import svLocale from '@meiko/react-intl/locale-data/sv';
import {handleUncaught} from 'io/errors';
import {logout} from 'modules/common/actions';
import {info} from 'modules/notifications/actions';
import {apiToken, activeOrganizationId} from 'modules/common/selectors';
import {medDur, longDur} from 'constants/notifications';
import {isApiTokenError} from 'utils/app';
import {formatMessageRaw} from 'io/app';
import {Chart} from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import AppBootstrapper from 'views/AppBootstrapper';
import {isLocalEnvironment} from 'utils/environment';

import 'styles/global.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'styles/react-datepicker.css'; // datepicker custom styles

// Initialize color scheme before React renders to prevent flash of wrong theme
import {applyColorScheme, initializeColorScheme} from 'utils/colorScheme';
const isCanaryRouteOnInitialLoad =
	typeof window !== 'undefined' && window.location?.pathname?.startsWith('/canary');
if (isCanaryRouteOnInitialLoad) {
	initializeColorScheme();
} else {
	// Ensure we don't accidentally leave the root dark class enabled for non-canary routes.
	applyColorScheme(false);
}

Chart.register(ChartDataLabels);
// Change default options for ALL charts
Chart.defaults.set('plugins.datalabels', {
	formatter: (value, context) => {
		if (value > 0) {
			return value;
		}
		return '';
	},
});

const isBenignResizeObserverMessage = message => {
	const msg = String(message || '');
	return (
		msg.includes('ResizeObserver loop limit exceeded') ||
		msg.includes('ResizeObserver loop completed with undelivered notifications')
	);
};

const isBenignResizeObserverError = evt => {
	const message = String(evt?.message || evt?.error?.message || '');
	return isBenignResizeObserverMessage(message);
};

const dismissReactErrorOverlay = () => {
	// CRA / react-error-overlay injects an iframe + exposes a global hook.
	try {
		const hook = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
		hook?.dismissRuntimeErrors?.();
	} catch {
		// ignore
	}

	try {
		const el = document.getElementById('react-error-overlay');
		if (el?.parentNode) el.parentNode.removeChild(el);
	} catch {
		// ignore
	}
};

// Some dev overlays hook into window.onerror directly; suppress this benign warning there too.
try {
	const prevOnError = window.onerror;
	window.onerror = (message, source, lineno, colno, error) => {
		if (
			isBenignResizeObserverMessage(message) ||
			isBenignResizeObserverMessage(error?.message)
		) {
			// Overlay may have already rendered; dismiss it.
			try {
				queueMicrotask(dismissReactErrorOverlay);
			} catch {
				setTimeout(dismissReactErrorOverlay, 0);
			}
			return true;
		}
		return typeof prevOnError === 'function'
			? Boolean(prevOnError(message, source, lineno, colno, error))
			: false;
	};
} catch {
	// Ignore assignment failures
}

try {
	const prevOnUnhandledRejection = window.onunhandledrejection;
	window.onunhandledrejection = evt => {
		const reason = evt?.reason;
		const message = String(reason?.message || reason || '');
		if (isBenignResizeObserverMessage(message)) {
			try {
				queueMicrotask(dismissReactErrorOverlay);
			} catch {
				setTimeout(dismissReactErrorOverlay, 0);
			}
			return true;
		}
		return typeof prevOnUnhandledRejection === 'function'
			? Boolean(prevOnUnhandledRejection(evt))
			: false;
	};
} catch {
	// Ignore assignment failures
}

window.addEventListener(
	'error',
	evt => {
		// Chrome can emit this during heavy layout measuring (e.g. drag-and-drop).
		// It is usually benign, but our global handler treats all window errors as fatal.
		if (isBenignResizeObserverError(evt)) {
			try {
				queueMicrotask(dismissReactErrorOverlay);
			} catch {
				setTimeout(dismissReactErrorOverlay, 0);
			}
			evt?.preventDefault?.();
			evt?.stopImmediatePropagation?.();
			return;
		}
		handleUncaught(evt.error);
	},
	// Capture so we can suppress before dev overlays.
	true,
);

window.addEventListener('unhandledrejection', evt => {
	const reason = evt?.reason;
	const message = String(reason?.message || reason || '');
	if (isBenignResizeObserverMessage(message)) {
		try {
			queueMicrotask(dismissReactErrorOverlay);
		} catch {
			setTimeout(dismissReactErrorOverlay, 0);
		}
		evt?.preventDefault?.();
		evt?.stopImmediatePropagation?.();
		return;
	}
	handleUncaught(reason);
});

// Dev overlays often listen to console.error for these ResizeObserver messages.
// Suppress only this known-benign case in local dev to avoid taking down the app.
if (isLocalEnvironment) {
	// Avoid double-wrapping if an early guard already patched console.error.
	if (!console.error?.__enioproResizeObserverFilter) {
		const originalError = console.error;
		const wrapped = (...args) => {
			const message = args
				.map(a => (a && typeof a === 'object' && 'message' in a ? a.message : a))
				.join(' ');
			if (isBenignResizeObserverMessage(message)) {
				dismissReactErrorOverlay();
				return;
			}
			originalError(...args);
		};
		wrapped.__enioproResizeObserverFilter = true;
		console.error = wrapped;
	}
}

const bugsnag = services.get('bugsnag');

// we handle bugsnag notifications manually from browser error handlers from now on
if (bugsnag) {
	window.stopBugsnagAutoNotify();
}

// TODO: first figure out how to bust its cache when updating app version
//registerServiceWorker();

addLocaleData(enLocale);
addLocaleData(fiLocale);
addLocaleData(svLocale);

const {instance: storeHook, enhancer: actionHookEnhancer} = createStoreActionHook();

const store = createStore({actionHookEnhancer});
services.set('store', store);

services.set('storeHook', storeHook);

if (bugsnag) {
	storeHook.subscribe((action, oldState, newState) => {
		bugsnag.leaveBreadcrumb('Redux action', {type: action.type});
	});
}

const onApiError = e => {
	if (isApiTokenError(e) && apiToken(store.getState())) {
		store.dispatch(logout());
		store.dispatch(
			info({
				id: 'api-401',
				message: formatMessageRaw('Session expired, new login required'),
				duration: medDur,
			}),
		);
	} else if (e.response && e.response.status === 503) {
		store.dispatch(
			info({
				id: 'api-503',
				message: formatMessageRaw(
					'The service is being updated. Please refresh the page in a few moments. The upgrade process may take a few minutes.',
				),
				duration: longDur,
			}),
		);
	}
};

const api = createApi({
	get: key => {
		const state = store.getState();
		return {
			apiToken: apiToken(state),
			organizationId: activeOrganizationId(state),
			onError: onApiError,
		}[key];
	},
});
services.set('api', api);

ReactDom.render(
	<QueryClientProvider client={queryClient}>
		<ReduxProvider store={store}>
			<IntlProvider>
				<ToastProvider>
					<Router history={services.get('history')}>
						<AppBootstrapper />
					</Router>
				</ToastProvider>
			</IntlProvider>
		</ReduxProvider>
		{isLocalEnvironment && <ReactQueryDevtools initialIsOpen={false} />}
	</QueryClientProvider>,
	document.getElementById('root'),
);
